import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Pagination from './Pagination';

const MasterUsers = ({ user: currentUser }) => {
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phone_number: '',
    role: 'admin',
    station_id: '',
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsers(currentPage);
    fetchStations();
  }, [currentPage]);

  const fetchStations = async () => {
    try {
      setStationsLoading(true);
      const response = await axios.get(`${API_BASE}/stations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStations(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch stations:', err);
    } finally {
      setStationsLoading(false);
    }
  };

  const fetchUsers = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/users?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.data.data || []);
      setTotalPages(response.data.data.last_page || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'is_active' ? value === 'true' : value,
    }));
  };

  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API_BASE}/users/${editingUser.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('User updated successfully');
      } else {
        // Create new user - no password needed, backend sets default
        const endpoint = currentUser?.role === 'superadmin' ? `${API_BASE}/users/admin` : `${API_BASE}/users`;
        await axios.post(endpoint, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('User created successfully');
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({
        fullname: '',
        email: '',
        phone_number: '',
        role: 'admin',
        station_id: stations.length > 0 ? String(stations[0].id) : '',
        is_active: true,
      });
      fetchUsers(1);
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (targetUser) => {
    setEditingUser(targetUser);
    setFormData({
      fullname: targetUser.fullname,
      email: targetUser.email,
      phone_number: targetUser.phone_number,
      role: targetUser.role,
      station_id: targetUser.station_id ? String(targetUser.station_id) : '',
      is_active: targetUser.is_active !== false,
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setDeletingId(id);
      try {
        await axios.delete(`${API_BASE}/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('User deleted successfully');
        fetchUsers(1);
        setCurrentPage(1);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleResetPassword = async (id, targetUser) => {
    if (window.confirm(`Reset password for ${targetUser.fullname}? Default password will be: ${targetUser.role}-${targetUser.phone_number}`)) {
      setResettingId(id);
      try {
        await axios.post(
          `${API_BASE}/auth/reset-password/${id}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccess(`Password for ${targetUser.fullname} has been reset successfully.`);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to reset password');
      } finally {
        setResettingId(null);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      fullname: '',
      email: '',
      phone_number: '',
      role: 'admin',
      station_id: stations.length > 0 ? String(stations[0].id) : '',
      is_active: true,
    });
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Master Users</h1>
        {!(showForm && editingUser) && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingUser(null);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Add User
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            {editingUser ? 'Edit User' : 'Create New User'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {currentUser?.role === 'superadmin' ? (
                    <>
                      <option value="superadmin">Superadmin</option>
                      <option value="admin">Admin</option>
                      <option value="sa">SA (Service Advisor)</option>
                      <option value="mechanic">Mechanic</option>
                    </>
                  ) : (
                    <>
                      <option value="admin">Admin</option>
                      <option value="sa">SA (Service Advisor)</option>
                      <option value="mechanic">Mechanic</option>
                    </>
                  )}
                </select>
                {currentUser?.role === 'admin' && (
                  <p className="text-xs text-blue-600 mt-1">
                    ℹ️ Users will be created under your station ({currentUser?.station_name || 'your AHASS'})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AHASS / Station *
                </label>
                <select
                  name="station_id"
                  value={formData.station_id}
                  onChange={handleInputChange}
                  required
                  disabled={stationsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select a station...</option>
                  {stations.map((station) => (
                    <option key={station.id} value={station.id}>
                      {station.name} ({station.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activation Status *
                </label>
                <select
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Deactivated</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              ℹ️ Default password will be: <strong>role-phone_number</strong> (e.g., admin-081234567890)
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  `${editingUser ? 'Update' : 'Create'} User`
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-medium transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      {!showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No users found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {u.fullname}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {u.phone_number}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.role === 'superadmin'
                              ? 'bg-purple-100 text-purple-800'
                              : u.role === 'admin'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {u.role.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.is_active !== false
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {u.is_active !== false ? 'ACTIVE' : 'DEACTIVATED'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2 flex">
                          <button
                            onClick={() => handleEdit(u)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleResetPassword(u.id, u)}
                            disabled={resettingId === u.id}
                            className="text-amber-600 hover:text-amber-900 font-medium disabled:opacity-50"
                          >
                            {resettingId === u.id ? 'Resetting...' : 'Reset Pass'}
                          </button>
                          <button
                            onClick={() => handleDelete(u.id)}
                            disabled={deletingId === u.id}
                            className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                          >
                            {deletingId === u.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterUsers;
